import { mergeStyleSets } from '@fluentui/react';
import { ColorIcon, PaymentCardIcon } from '@fluentui/react-icons-mdl2';
import { AppsIcon, MenuIcon, ProviderConsumer, TeamCreateIcon, Text, ThemePrepared, Tooltip } from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import { Icon } from '@fluentui/react/lib/Icon';
import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { ThemeColorScheme } from '../../shared/common/TeamsTheme';
import { Route } from '../../../route-constants';
import { MyGroupWorkIcon } from '../../../svg';
import AutomationIcon from '../../../svg/automation.svg';
import CategoryIcon from '../../../svg/category.svg';
import OpenAiIconDark from '../../../svg/fileTypes/dark/openai.svg';
import OpenAiIconLight from '../../../svg/fileTypes/light/openai.svg';
import LifecycleIcon from '../../../svg/lifecycle-icon-settings-nav.svg';
import PriorityIcon from '../../../svg/priority-icon.svg';
import ResolutionCategoryIcon from '../../../svg/resolution-category.svg';
import ServiceOffering from '../../../svg/service-offering.svg';
import SLAIcon from '../../../svg/SLA-icon.svg';
import StatusIcon from '../../../svg/status.svg';
import Tikit from '../../../svg/ticket-neutral-icon.svg';
import TicketTypeIcon from '../../../svg/ticket-type.svg';
import FormIcon from '../../../svg/forms-icon.svg';
import FormIconDark from '../../../svg/forms-icon-dark.svg';
import { checkInTeams } from '../../App';
import { appState, AppStateContext, useSetState } from '../../AppState';
import { getCachedFeature } from '../../shared/cache/FeatureCache';
import { getNodesByType, NodeType } from '../../shared/cache/NodeCache';
import { GetArrangeSettingsKey } from '../../shared/common/CommonHelper';
import { CheckFeature } from '../../shared/components/CheckFeature';
import eventBus from '../../shared/services/eventBus.service';
import { ADMINISTRATORS, AGENTS, ANALYSTS, ARRANGED_AGENT_SETTING_KEYS, ARRANGED_END_USER_SETTING_KEYS, ARRANGED_SETTING_KEYS_TEAMS, ARRANGED_SUPER_AGENT_SETTING_KEYS, ENDPOINT_SECURITY_MAPPINGS, KNOWLEDGEAGENT, KNOWLEDGEREADER, LICENSE_FEATURE, SETTINGS_PAGES, SIDENAV_AGENTS_KEYS, SIDENAV_ARRANGED_KEYS, SIDENAV_KBAGENTS_KEYS } from '../../shared/utils/constants';
import { i18n } from '../../shared/utils/helper';
import { getViewIdFromStorage } from '../../tikit/toolbar/CustomViewPopup/heplers/customPopupHelper';
import CustomPropertiesIcon from '../../../svg/custom-properties-outlined.svg';

const { t } = i18n();

const classNames = mergeStyleSets({
  sideNavCollapsed: {
    width: '48px !important',
    minWidth: '48px !important'
  },

  sideNavActive: {
    backgroundColor: 'var(--mgt-theme-brand-background1)'
  },
  sideNameIcon: {
    fill: 'var(--mgt-theme-foregroundFocus)',
    marginRight: '-4px',
  },
  sideNav: {
    height: '100vh',
    width: '221px',
    minWidth: '221px',
    maxWidth: '221px',
    transition: '0.2s width ease-in-out',
    borderRight: '1px solid var(--mgt-theme-border-color)'
  }
});

interface Props {
  settingsPage?: string;
  hideSettingsNav?: boolean;
  active: string;
}

export interface SideNavProps {
  key: string;
  path: string;
  teamsPath?: string;
  iconElement: JSX.Element;
  title: string;
  newtab?: boolean;
  external?: boolean;
  group: string;
}

export const sidenav_unarranged: SideNavProps[] = [
  {
    key: 'tickets',
    path: '/',
    iconElement: <Icon iconName="Ticket" className={`text-lg`} />,
    title: t('side-nav.ticket'),
    group: 'main'
  },
  {
    key: 'mywork',
    path: Route.myWork.pagePath,
    iconElement: <Icon iconName="WorkItem" className={`text-lg`} />,
    title: t('side-nav.my-work'),
    group: 'main'
  },
  {
    key: 'mygroupwork',
    path: Route.myGroupWork.pagePath,
    iconElement: <MyGroupWorkIcon />,
    title: t('side-nav.my-group-work'),
    group: 'main'
  },
  {
    key: SETTINGS_PAGES.USERS,
    path: `/${SETTINGS_PAGES.USERS}`,
    teamsPath: `/settings/${SETTINGS_PAGES.USERS}`,
    iconElement: <Icon iconName="ContactList" className={`text-lg`} />,
    title: t('side-nav.users'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.KNOWLEDGE,
    path: `/${SETTINGS_PAGES.KNOWLEDGE}`,
    teamsPath: `/settings/${SETTINGS_PAGES.KNOWLEDGE}`,
    iconElement: <Icon iconName="KnowledgeArticle" className={`text-lg`} />,
    title: t('side-nav.knowledge'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.SECURITY,
    path: `/settings/${SETTINGS_PAGES.SECURITY}`,
    iconElement: <Icon iconName="SecurityGroup" />,
    title: t('side-nav.security'),
    group: 'security'
  },
  {
    key: SETTINGS_PAGES.ACCESS_TOKEN,
    path: `/settings/${SETTINGS_PAGES.ACCESS_TOKEN}`,
    iconElement: <Icon iconName="Signin" />,
    title: t('side-nav.access-token'),
    group: 'security'
  },
  {
    key: SETTINGS_PAGES.GROUPS,
    path: `/settings/${SETTINGS_PAGES.GROUPS}`,
    iconElement: <Icon iconName="Group" />,
    title: t('side-nav.groups'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.TAGS,
    path: `/settings/${SETTINGS_PAGES.TAGS}`,
    iconElement: <Icon iconName="Tag" />,
    title: t('side-nav.tags'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.NOTIFICATION_CENTER,
    path: `/settings/${SETTINGS_PAGES.NOTIFICATION_CENTER}`,
    iconElement: <Icon iconName="PublicEmail" />,
    title: t('side-nav.notification-center'),
    group: 'settings'
  },
  {
    key: SETTINGS_PAGES.EMAIL_CONNECTOR,
    path: `/settings/${SETTINGS_PAGES.EMAIL_CONNECTOR}`,
    iconElement: <Icon iconName="PublicEmail" />,
    title: t('side-nav.email-connector'),
    group: 'settings'
  },
  {
    key: SETTINGS_PAGES.FEATURE_CONFIGURATION,
    path: `/settings/${SETTINGS_PAGES.FEATURE_CONFIGURATION}`,
    iconElement: <Icon iconName="Shield" />,
    title: t('side-nav.consent-management'),
    group: 'settings'
  },
  {
    key: SETTINGS_PAGES.BOT_CONFIGURATION,
    path: `/settings/${SETTINGS_PAGES.BOT_CONFIGURATION}`,
    iconElement: <Icon iconName="TeamsLogoInverse" />,
    title: t('side-nav.bot-configuration'),
    group: 'settings'
  },
  {
    key: SETTINGS_PAGES.AI_CONFIG,
    path: `/settings/${SETTINGS_PAGES.AI_CONFIG}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => {
          return state.theme === themeNames.Default ? (
            <OpenAiIconLight className="fill-current text-black" />
          ) : (
            <OpenAiIconDark className="fill-current text-white" />
          );
        }}
      </AppStateContext.Consumer>
    ),
    title: t('side-nav.ai-configuration'),
    group: 'settings'
  },
  {
    key: SETTINGS_PAGES.BILLING,
    path: `/settings/${SETTINGS_PAGES.BILLING}`,
    iconElement: <PaymentCardIcon className='setting-icon' />,
    title: t('side-nav.billing'),
    group: 'others'
  },
  {
    key: SETTINGS_PAGES.TEMPLATES,
    path: `/settings/${SETTINGS_PAGES.TEMPLATES}`,
    iconElement: <Icon iconName="FileTemplate" />,
    title: t('side-nav.templates'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.PREFERENCES,
    path: `/settings/${SETTINGS_PAGES.PREFERENCES}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<ColorIcon className={`setting-icon fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>
    ),
    title: t('side-nav.preferences'),
    group: 'others'
  },
  {
    key: SETTINGS_PAGES.AUTOMATION,
    path: `/settings/${SETTINGS_PAGES.AUTOMATION}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<AutomationIcon className={`fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>
    ),
    title: t('side-nav.automation'),
    group: 'settings'
  },
  {
    key: SETTINGS_PAGES.STATUS,
    path: `/settings/${SETTINGS_PAGES.STATUS}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<StatusIcon className={`fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>
    ),
    title: t('side-nav.status'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.PRIORITY,
    path: `/settings/${SETTINGS_PAGES.PRIORITY}`,
    iconElement: (<PriorityIcon />),
    title: t('side-nav.priority'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.CATEGORY,
    path: `/settings/${SETTINGS_PAGES.CATEGORY}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<CategoryIcon className={`fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>
    ),
    title: t('side-nav.category'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.RESOLUTIONCATEGORY,
    path: `/settings/${SETTINGS_PAGES.RESOLUTIONCATEGORY}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<ResolutionCategoryIcon className={`fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>
    ),
    title: t('resolution-note.resolution-category'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.TICKET_TYPES,
    path: `/settings/${SETTINGS_PAGES.TICKET_TYPES}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<TicketTypeIcon className={`fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>
    ),
    title: t('side-nav.ticket-types'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.LIFECYCLE,
    path: `/settings/${SETTINGS_PAGES.LIFECYCLE}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<LifecycleIcon className={`fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>
    ),
    title: t('side-nav.lifecycle'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.SLA,
    path: `/settings/${SETTINGS_PAGES.SLA}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<SLAIcon className={`fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>),
    title: t('side-nav.sla'),
    group: 'settings'
  },
  {
    key: SETTINGS_PAGES.TEAMS,
    path: `/settings/${SETTINGS_PAGES.TEAMS}`,
    iconElement: (<TeamCreateIcon outline />),
    title: t('side-nav.teams'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.TIKIT_CONFIGURATION,
    path: `/settings/${SETTINGS_PAGES.TIKIT_CONFIGURATION}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<Tikit className={`fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>
    ),
    title: t('side-nav.ticket-configuration'),
    group: 'settings'
  },
  {
    key: SETTINGS_PAGES.SERVICE_OFFERING,
    path: `/settings/${SETTINGS_PAGES.SERVICE_OFFERING}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<ServiceOffering className={`fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>
    ),
    title: t('service-offering.menu-title'),
    group: 'settings'
  },
  {
    key: SETTINGS_PAGES.FORMS,
    path: `/settings/${SETTINGS_PAGES.FORMS}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) =>
          state.theme === themeNames.Default ? (
            <FormIcon className="fill-current text-black" />
          ) : (
            <FormIconDark className="fill-current text-white" />
          )
        }
      </AppStateContext.Consumer>
    ),
    title: t('side-nav.forms'),
    group: 'config'
  },
  {
    key: SETTINGS_PAGES.CUSTOM_PROPERTIES,
    path: `/settings/${SETTINGS_PAGES.CUSTOM_PROPERTIES}`,
    iconElement: (
      <AppStateContext.Consumer>
        {(state) => (<CustomPropertiesIcon className={`fill-current ${state.theme === themeNames.Default ? "text-black" : "text-white"}`} />)}
      </AppStateContext.Consumer>),
    title: t('side-nav.custom-properties'),
    group: 'config'
  },
];
export const SideNav = (props: Props) => {
  let { settingsKey } = useParams<any>();
  const [sideNavItems, setSideNavItems] = useState<SideNavProps[]>([]);
  const [settingsArranged, setSettingsArranged] = useState([]);
  const [settingsArrangedTeams, setSettingsArrangedTeams] = useState([]);
  let setState = useSetState();
  const history = useHistory();
  let settingsKeyStart = 3;
  const currentState = appState();

  useEffect(() => {
    // Show the role-based items straight away rather than waiting on getSettingsNavByRole (which
    // itself awaits getCachedFeature - a cold-cache/slow-backend call) or the license/custom-node
    // checks below - those can each take seconds, and there is no reason the whole nav should stay
    // blank while any of them resolve.
    const baseItems = getBaseSideNavByRole();
    setSideNavItems(baseItems);
    let cancelled = false;

    (async () => {
      const settingsItems = await getSettingsNavByRole();
      if (cancelled) return;
      setSettingsArranged(settingsItems);
      setSettingsArrangedTeams(ARRANGED_SETTING_KEYS_TEAMS.map(key => sidenav_unarranged.filter(x => x.key === key)[0]));

      const keyStart = sidenav_unarranged.findIndex(x => x.key === props.active);
      if (keyStart >= settingsKeyStart) {
        setState(state => {
          return {
            ...state,
            isSettingsSidenavCollapsed: false
          };
        });
      }
    })();

    (async () => {
      const refinedItems = await refineSideNavByRole(baseItems);
      if (!cancelled) setSideNavItems(refinedItems);
    })();

    const updateItems = () => {
      setState(state => { return { ...state }; });
    };
    eventBus.on('applicationRolesUpdated', updateItems);

    return () => {
      cancelled = true;
      eventBus.remove('applicationRolesUpdated', updateItems)
    };
    // A direct URL load can reach this mount before meAuthentication() has populated
    // userRoles.roles - re-run once roles arrive instead of building the nav off an empty array
    // that this effect would otherwise never revisit. Keyed on presence rather than the array
    // reference so a later re-render with an equivalent (but reference-different) roles array
    // doesn't re-trigger this unnecessarily.
  }, [currentState.userRoles.roles.length > 0]);


  const getBaseSideNavByRole = (): SideNavProps[] => {
    let workItems: SideNavProps[] = [];
    if (currentState.userRoles.roles.includes(ADMINISTRATORS) || currentState.userRoles.roles.includes(ANALYSTS))
      workItems = SIDENAV_ARRANGED_KEYS.map(key => sidenav_unarranged.filter(x => x.key === key)[0]);
    else if (currentState.userRoles.roles.includes(AGENTS)) {
      workItems = SIDENAV_AGENTS_KEYS.map(key => sidenav_unarranged.filter(x => x.key === key)[0]);

      if (currentState.userRoles.roles.includes(KNOWLEDGEAGENT) || currentState.userRoles.roles.includes(KNOWLEDGEREADER)) {
        SIDENAV_KBAGENTS_KEYS.map(key => sidenav_unarranged.filter(x => x.key === key)[0]).forEach(item => {
          workItems.splice(3, 0, item);
        });
      }
    }
    else if (currentState.userRoles.roles.includes(KNOWLEDGEAGENT) || currentState.userRoles.roles.includes(KNOWLEDGEREADER))
      workItems = SIDENAV_KBAGENTS_KEYS.map(key => sidenav_unarranged.filter(x => x.key === key)[0]);

    return workItems;
  };

  const refineSideNavByRole = async (baseItems: SideNavProps[]): Promise<SideNavProps[]> => {
    // baseItems is already in state, so pushing onto it and setting it back is a render React skips:
    // same reference, no change, and the custom nodes only appear if something else re-renders. #35375
    let workItems = [...baseItems];

    const [enabled, groupEnabled] = await Promise.all([getCachedFeature(LICENSE_FEATURE.MyWork), getCachedFeature(LICENSE_FEATURE.MyGroupWork)]);
    if (!enabled) workItems = workItems.filter(i => i.key != 'mywork')
    if (!groupEnabled) workItems = workItems.filter(i => i.key != 'mygroupwork')

    const nodes = await getNodesByType(NodeType.Page);
    nodes?.forEach(n => workItems.push({
      key: `node-${n.Id}`,
      path: `/customnode/${n.Id}`,
      iconElement: n.Icon && n.Icon !== '' ? <img src={n.Icon} style={{ minWidth: "20px", minHeight: "20px", width: "20px" }} /> : <AppsIcon size='large' outline />,
      title: n.Name, group: 'main'
    }));

    return workItems;
  };


  const getSettingsNavByRole = async () => {
    if (currentState.userRoles.roles.includes(ADMINISTRATORS)) {
      let keys = await GetArrangeSettingsKey();
      return keys.map(key => sidenav_unarranged.filter(x => x.key === key)[0]);
    }
    else if (currentState.userRoles.roles.includes(ANALYSTS)) {
      return ARRANGED_SUPER_AGENT_SETTING_KEYS.map(key => sidenav_unarranged.filter(x => x.key === key)[0]);
    }
    else if (currentState.userRoles.roles.includes(AGENTS)) {
      return ARRANGED_AGENT_SETTING_KEYS.map(key => sidenav_unarranged.filter(x => x.key === key)[0]);
    }
    else {
      return ARRANGED_END_USER_SETTING_KEYS.map(key => sidenav_unarranged.filter(x => x.key === key)[0]);
    }
  }

  const pageNavigateHandler = (key: string, path: string) => {
    // path is updated path and location.pathname is previous pathname
    if (path === location.pathname) {
      return
    }

    let updatedpath = path;
    if (path === '/') {
      const lastTicketPage = localStorage.getItem('lastTicketPage') || 'table';
      const newPath = lastTicketPage === 'table' ? '/' : '/tickets/board'; // lastTicketPage can only be table or board
      const localCustomViewId = getViewIdFromStorage('custom');
      const queryParam = location.search && localCustomViewId ? `?id=${localCustomViewId}` : '';
      updatedpath = queryParam ? `${newPath}${queryParam}` : newPath;
    }
    history.push(updatedpath);
  }

  const renderSideNavItems = (isSidenavCollapsed?: boolean) => {
    const sidenav_arranged = sideNavItems || [];
    return sidenav_arranged.map((nav, i) => (
      <li key={nav.key} className={props.active === nav.key ? classNames.sideNavActive : ``}>
        <div onClick={() => pageNavigateHandler(nav.key, nav.path)}>
          <a className={`cursor-pointer px-4 py-2 block`}>
            <div className={`flex items-center`}>
              <Tooltip trigger={<span>{nav.iconElement}</span>} content={nav.title} />
              <Text
                content={nav.title}
                className={`pl-2 truncate ${isSidenavCollapsed ? 'hidden' : ''}`}
                weight={props.active === nav.key ? `semibold` : `regular`}
              />
            </div>
          </a>
        </div>
      </li>
    ));
  };

  const arrayToLink = (sidenavprops: SideNavProps[], globalTheme: ThemePrepared<any>) => {
    let prevGroup = null;
    return sidenavprops.map(nav => {
      const check = ENDPOINT_SECURITY_MAPPINGS.find(x => x.key == nav.key);
      if (check && JSON.parse(sessionStorage.getItem('my_application_roles') || '[]').filter(x => x.EndpointPath == check.path && x.Permissions.includes(check.method)).length <= 0)
        return null;

      const separator = (prevGroup !== null && prevGroup !== nav.group)
      prevGroup = nav.group;

      let returnLi = (<li key={nav.key} className={`${settingsKey === nav.key ? classNames.sideNavActive : ''} ${separator ? 'border-t' : ''}`} style={{
        borderLeft: settingsKey === nav.key ? `solid 3px ${globalTheme.siteVariables.colorScheme.default.foregroundActive}` : `solid 3px transparent`,
        borderTopColor: 'var(--mgt-theme-border-color)'
      }}>
        <Link to={checkInTeams() ? nav.teamsPath || nav.path : nav.path} className={`cursor-pointer px-4 py-2 block`}>
          <div className={`flex items-center`}>
            {nav.iconElement}
            <Text
              content={nav.title}
              className={`pl-2 truncate`}
              weight={settingsKey === nav.key ? `semibold` : `regular`}
            />
          </div>
        </Link>
      </li>
      );
      if (nav.key == SETTINGS_PAGES.AI_CONFIG) {
        return <CheckFeature featureName={LICENSE_FEATURE.ConversationalAi}>
          {returnLi}
        </CheckFeature>;
      }

      return returnLi;
    }).filter(x => x !== null);
  }

  const renderSettingSideNavItems = (globalTheme: ThemePrepared<any>) => {
    if (checkInTeams()) {
      return arrayToLink(settingsArrangedTeams, globalTheme);
    } else {
      return arrayToLink(settingsArranged, globalTheme);
    }
  };

  return (
    <AppStateContext.Consumer>
      {state => {
        return (
          <ProviderConsumer
            render={globalTheme => (
              <>
                {!checkInTeams() && <div style={{ textDecoration: 'none', ...ThemeColorScheme(globalTheme.siteVariables) }} className={`${classNames.sideNav} ${state.isSidenavCollapsed ? classNames.sideNavCollapsed : ''} browser-sidenav print-hidden`}>
                  <ul>
                    <li className={'hidden md:block'}>
                      <a className={`cursor-pointer p-2 m-2 inline-block`} onClick={() => { setState(s => { return { ...s, isSidenavCollapsed: !s.isSidenavCollapsed }; }); }}>
                        <MenuIcon />
                      </a>
                    </li>
                    {renderSideNavItems(state.isSidenavCollapsed)}
                  </ul>
                </div>}
                {(!state.isLoading) && (
                  <div className={`hidden ${(props.hideSettingsNav || typeof props.hideSettingsNav !== "undefined") ? '' : 'md:block'} border-r`} style={{ width: '300px', ...ThemeColorScheme(globalTheme.siteVariables) }}>
                    <ul className="settings-menu overflow-auto" style={{ height: checkInTeams() ? '100vh' : 'calc(100vh - 50px)' }}>{renderSettingSideNavItems(globalTheme)}</ul>
                  </div>
                )}
              </>
            )}
          />
        );
      }}
    </AppStateContext.Consumer>
  );
};
